import React from "react";
import { Routes, Route, Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import MainLayout from "../components/Layout/MainLayout";
import DashboardLayout from "../components/Layout/DashboardLayout";

// Halaman Utama
import Home from "../pages/Home";
import About from "../pages/About";
import Gallery from "../pages/Gallery";
import Contact from "../pages/Contact";
import Login from "../pages/Login";
import Register from "../pages/Register";
import ForgotPassword from "../pages/ForgotPassword";
import Marketplace from "../pages/Marketplace";
import ProductDetail from "../pages/ProductDetail";
import Cart from "../pages/Cart";
import Checkout from "../pages/Checkout";
import OrderSuccess from "../pages/OrderSuccess";
import BlogIndex from "../pages/BlogIndex";
import BlogDetail from "../pages/BlogDetail";

// Halaman Dashboard
import DashboardHome from "../pages/Dashboard/DashboardHome";
import ProductsPage from "../pages/Dashboard/ProductsPage";
import CategoriesPage from "../pages/Dashboard/CategoriesPage";
import OrdersPage from "../pages/Dashboard/OrdersPage";
import BlogsPage from "../pages/Dashboard/BlogsPage";
import MediaPage from "../pages/Dashboard/MediaPage";
import UsersPage from "../pages/Dashboard/UsersPage";
import MessagesPage from "../pages/Dashboard/MessagesPage";
import SettingsPage from "../pages/Dashboard/SettingsPage";

// Halaman Kategori
import Mikrokontroler from "../pages/categories/Mikrokontroler";
import ModulKomponen from "../pages/categories/ModulKomponen";
import PaketStarterKit from "../pages/categories/PaketStarterKit";
import SensorAirFlowmeter from "../pages/categories/SensorAirFlowmeter";
import SensorGasUdara from "../pages/categories/SensorGasUdara";
import SensorSuhuKelembaban from "../pages/categories/SensorSuhuKelembaban";
import SensorTanahPertanian from "../pages/categories/SensorTanahPertanian";

// --- IMPORT HALAMAN SOLUSI YANG SUDAH DIBUAT ---
// Pastikan nama file (DepotAirCerdas.jsx, dll) dan foldernya benar
import SmartWaterMonitor from "../pages/categories/SmartWaterMonitor";
import DepotAirCerdas from "../pages/categories/DepotAirCerdas";
import SmartParking from "../pages/categories/SmartParking";
import SmartFarming from "../pages/categories/SmartFarming";
import SmartHome from "../pages/categories/SmartHome";
import Konsultasi from "../pages/categories/Konsultasi"; // Asumsi nama file untuk form konsultasi

function PlaceholderPage({
  title,
  description = "Halaman ini masih dalam tahap pengembangan dan akan segera tersedia.",
}) {
  return (
    <div className="min-h-[60vh] p-6 md:p-10 flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white p-8 shadow-sm text-center dark:border-gray-700 dark:bg-gray-800">
        <div className="text-5xl mb-4">🚧</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{title}</h1>
        <p className="text-gray-600 dark:text-gray-300">{description}</p>
        <Link to="/" className="mt-6 inline-block rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors">
          Kembali ke Beranda
        </Link>
      </div>
    </div>
  );
}

function ProtectedRoute() {
  const { isAuthenticated, authReady } = useAuth();
  const location = useLocation();

  if (!authReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-4 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          Memuat autentikasi...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function NotFoundPage() {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center p-6 text-center bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <h1 className="text-6xl font-extrabold text-blue-600 dark:text-blue-400 mb-2">404</h1>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Halaman Tidak Ditemukan</h2>
      <p className="text-gray-600 dark:text-gray-300 max-w-md">
        Maaf, halaman yang Anda cari mungkin telah dihapus, namanya diubah, atau sementara tidak tersedia.
      </p>
      <Link
        to="/"
        className="mt-8 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors shadow-lg"
      >
        Kembali ke Beranda
      </Link>
    </div>
  );
}

export default function AppRouter() {
  return (
    <Routes>
      <Route element={<MainLayout />}>
        {/* Rute Utama */}
        <Route index element={<Home />} />

        {/* Alias Rute Bahasa Indonesia sebagai Default */}
        <Route path="/tentang-kami" element={<About />} />
        <Route path="/about" element={<Navigate to="/tentang-kami" replace />} />

        <Route path="/galeri" element={<Gallery />} />
        <Route path="/gallery" element={<Navigate to="/galeri" replace />} />
        <Route path="/proyek-kami" element={<Navigate to="/galeri" replace />} />

        <Route path="/kontak" element={<Contact />} />
        <Route path="/contact" element={<Navigate to="/kontak" replace />} />
        <Route path="/kontak/formulir" element={<Navigate to="/kontak" replace />} />

        {/* Toko / Blog */}
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/produk/:slug" element={<ProductDetail />} />
        <Route path="/product/:slug" element={<Navigate to="/produk/:slug" replace />} />
        
        <Route path="/keranjang" element={<Cart />} />
        <Route path="/cart" element={<Navigate to="/keranjang" replace />} />
        
        <Route path="/checkout" element={<Checkout />} />
        
        <Route path="/pesanan-berhasil" element={<OrderSuccess />} />
        <Route path="/order-success" element={<Navigate to="/pesanan-berhasil" replace />} />

        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />

        {/* --- RUTE SOLUSI (DIUBAH DARI PLACEHOLDER KE KOMPONEN ASLI) --- */}
        {/* Rute Bahasa Indonesia (Primary) */}
        <Route path="/solusi/depot-air-cerdas" element={<DepotAirCerdas />} />
        <Route path="/solusi/smart-parking" element={<SmartParking />} />
        <Route path="/solusi/smart-farming" element={<SmartFarming />} />
        <Route path="/solusi/smart-home" element={<SmartHome />} />
        <Route path="/solusi/watermonitor" element={<SmartWaterMonitor />} />
        <Route path="/solusi/watermonitor" element={<Navigate to="/solusi/watermonitor" replace />} />
        <Route path="/solusi/konsultasi" element={<Konsultasi />} />

        {/* Redirect dari Bahasa Inggris (Fallback) */}
        <Route path="/solutions/depot-air-cerdas" element={<Navigate to="/solusi/depot-air-cerdas" replace />} />
        <Route path="/solutions/smart-parking" element={<Navigate to="/solusi/smart-parking" replace />} />
        <Route path="/solutions/smart-farming" element={<Navigate to="/solusi/smart-farming" replace />} />
        <Route path="/solutions/smart-home" element={<Navigate to="/solusi/smart-home" replace />} />
        <Route path="/solutions/watermonitor" element={<Navigate to="/solusi/watermonitor" replace />} />
        <Route path="/solutions/consultation" element={<Navigate to="/solusi/konsultasi" replace />} />


        {/* --- RUTE KATEGORI (DIUBAH KE BAHASA INDONESIA) --- */}
        <Route path="/kategori/mikrokontroler" element={<Mikrokontroler />} />
        <Route path="/kategori/modul-komponen" element={<ModulKomponen />} />
        <Route path="/kategori/paket-starter-kit" element={<PaketStarterKit />} />
        <Route path="/kategori/sensor-air-flowmeter" element={<SensorAirFlowmeter />} />
        <Route path="/kategori/sensor-gas-udara" element={<SensorGasUdara />} />
        <Route path="/kategori/sensor-suhu-kelembaban" element={<SensorSuhuKelembaban />} />
        <Route path="/kategori/sensor-tanah-pertanian" element={<SensorTanahPertanian />} />

        {/* Kategori yang belum ada file-nya, tetap pakai Placeholder */}
        <Route path="/kategori/sensor-jarak-gerak" element={<PlaceholderPage title="Sensor Jarak & Gerak" />} />
        <Route path="/kategori/sensor-lainnya" element={<PlaceholderPage title="Sensor Lainnya" />} />

        {/* Redirect dari URL bahasa inggris lama ke bahasa indonesia */}
        <Route path="/categories/mikrokontroler" element={<Navigate to="/kategori/mikrokontroler" replace />} />
        <Route path="/categories/modul-komponen" element={<Navigate to="/kategori/modul-komponen" replace />} />
        <Route path="/categories/paket-starter-kit" element={<Navigate to="/kategori/paket-starter-kit" replace />} />
        <Route path="/categories/sensor-air-flowmeter" element={<Navigate to="/kategori/sensor-air-flowmeter" replace />} />
        <Route path="/categories/sensor-gas-udara" element={<Navigate to="/kategori/sensor-gas-udara" replace />} />
        <Route path="/categories/sensor-suhu-kelembaban" element={<Navigate to="/kategori/sensor-suhu-kelembaban" replace />} />
        <Route path="/categories/sensor-suhu-kelembapan" element={<Navigate to="/kategori/sensor-suhu-kelembaban" replace />} />
        <Route path="/categories/sensor-tanah-pertanian" element={<Navigate to="/kategori/sensor-tanah-pertanian" replace />} />
        <Route path="/categories/sensor-jarak-gerak" element={<Navigate to="/kategori/sensor-jarak-gerak" replace />} />
        <Route path="/categories/sensor-lainnya" element={<Navigate to="/kategori/sensor-lainnya" replace />} />

        {/* Autentikasi */}
        <Route path="/masuk" element={<Login />} />
        <Route path="/login" element={<Navigate to="/masuk" replace />} />
        
        <Route path="/daftar" element={<Register />} />
        <Route path="/register" element={<Navigate to="/daftar" replace />} />
        
        <Route path="/lupa-kata-sandi" element={<ForgotPassword />} />
        <Route path="/forgot-password" element={<Navigate to="/lupa-kata-sandi" replace />} />
      </Route>

      {/* Rute Dashboard (Dilindungi) */}
      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          <Route path="/dashboard" element={<DashboardHome />} />
          <Route path="/dashboard/produk" element={<ProductsPage />} />
          <Route path="/dashboard/products" element={<Navigate to="/dashboard/produk" replace />} />
          
          <Route path="/dashboard/kategori" element={<CategoriesPage />} />
          <Route path="/dashboard/categories" element={<Navigate to="/dashboard/kategori" replace />} />
          
          <Route path="/dashboard/pesanan" element={<OrdersPage />} />
          <Route path="/dashboard/orders" element={<Navigate to="/dashboard/pesanan" replace />} />
          
          <Route path="/dashboard/blogs" element={<BlogsPage />} />
          <Route path="/dashboard/media" element={<MediaPage />} />
          
          <Route path="/dashboard/pengguna" element={<UsersPage />} />
          <Route path="/dashboard/users" element={<Navigate to="/dashboard/pengguna" replace />} />
          
          <Route path="/dashboard/pengaturan" element={<SettingsPage />} />
          <Route path="/dashboard/settings" element={<Navigate to="/dashboard/pengaturan" replace />} />
          
          <Route path="/dashboard/pesan" element={<MessagesPage />} />
          <Route path="/dashboard/messages" element={<Navigate to="/dashboard/pesan" replace />} />
        </Route>
      </Route>

      {/* Rute Tidak Ditemukan */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}